import React from 'react';
import {
  Datagrid,
  List,
  TextField,
  TextInput,
  SelectInput,
  DateField,
  ReferenceInput,
  FunctionField
} from 'react-admin';
import { Breadcrumbs, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import {
  OPPORTUNITY_STATE_CHOICES,
  OPPORTUNITY_STATUS_LABELS
} from '../types/OpportunityStatusChoices.ts';
import { RecordRowActions } from '../components/record/RecordRowActions.tsx';
import { ListActionsWithImport } from '../data-import/ImportButton.tsx';

const OpportunityList: React.FC = () => {
  const opportunityFilters = [
    <TextInput key="search" label="Search" source="q" alwaysOn />,
    <SelectInput
      key="state"
      source="state"
      label="Status"
      choices={OPPORTUNITY_STATE_CHOICES}
      alwaysOn
    />,
    <ReferenceInput
      key="stage"
      source="opportunityStageId"
      reference="opportunity-stages"
      label="Stage"
      alwaysOn
    >
      <SelectInput optionText="name" />
    </ReferenceInput>
  ];

  type OpportunityRecord = {
    status: keyof typeof OPPORTUNITY_STATUS_LABELS;
    contact?: {
      company?: {
        id?: number;
        name?: string | null;
      } | null;
    } | null;
  };

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Opportunities</Typography>
      </Breadcrumbs>
      <List
        filters={opportunityFilters}
        filterDefaultValues={{ state: 'open' }}
        sort={{ field: 'createdAt', order: 'DESC' }}
        actions={<ListActionsWithImport kind="opportunities" />}
      >
        <Datagrid rowClick="show" bulkActionButtons={false}>
          <TextField source="id" />
          <TextField source="contactFullName" label="Contact" />
          <FunctionField
            label="Company"
            render={(record: OpportunityRecord) => {
              const companyId = record.contact?.company?.id;
              const companyName = record.contact?.company?.name?.trim();
              if (!companyName) {
                return '-';
              }

              return companyId ? (
                <MuiLink
                  component={RouterLink}
                  to={`/companies/${companyId}/show`}
                  onClick={(event) => event.stopPropagation()}
                >
                  {companyName}
                </MuiLink>
              ) : (
                companyName
              );
            }}
          />
          <FunctionField
            label="Status"
            render={(record: OpportunityRecord) => {
              const label = OPPORTUNITY_STATUS_LABELS[record.status];
              return label ?? record.status;
            }}
          />
          <TextField source="opportunityStage.name" label="Stage" />
          <TextField source="opportunityServiceNames" label="Programs" />
          <DateField
            source="dateOpened"
            label="Opened"
            locales="en-US"
            options={{ year: 'numeric', month: 'short', day: 'numeric' }} // e.g. Jul 21, 2025
          />
          <DateField
            source="dateClosed"
            label="Closed"
            locales="en-US"
            options={{ year: 'numeric', month: 'short', day: 'numeric' }}
          />

          <RecordRowActions showDelete />
        </Datagrid>
      </List>
    </Container>
  );
};

export default OpportunityList;
